/**
 * Phase 1b · Slice 2a · Reports batch UI panels.
 *
 * Each panel:
 *  • Fetches its data from a dedicated `/api/admin/sales/*` endpoint.
 *  • Reconciles against its drill-down (via test coverage on the API).
 *  • Preserves every `"unknown"` bucket returned by the backend — never
 *    hides it, never merges it into a real bucket.
 *  • Is admin-only (auth enforced by the router).
 *
 * No production writes. No inference. No backfill.
 */
import React, { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { formatCents as DOLLAR, UNKNOWN_LABEL } from "./adminSalesHelpers";

export { DOLLAR, UNKNOWN_LABEL };

function useSalesEndpoint(path, deps) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let cancelled = false;
    setLoading(true); setErr(null);
    api.get(path)
      .then(r => { if (!cancelled) setData(r.data); })
      .catch(e => { if (!cancelled) setErr(formatApiError(e?.response?.data?.detail) || "Failed"); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
  return { data, err, loading };
}

// ── Trend chart ──────────────────────────────────────────────────
export function TrendPanel({ preset, dateBasis }) {
  const [granularity, setGranularity] = useState("day");
  const { data, err, loading } = useSalesEndpoint(
    `/admin/sales/trend?preset=${preset}&date_basis=${dateBasis}&granularity=${granularity}`,
    [preset, dateBasis, granularity],
  );
  const maxCT = useMemo(
    () => Math.max(1, ...(data?.buckets || []).map(b => b.customer_total || 0)),
    [data],
  );
  return (
    <section className="rounded-2xl border-2 border-[var(--ink)] bg-white p-4 space-y-3"
             data-testid="trend-panel">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h2 className="font-display text-lg">Sales Trend</h2>
        <div className="flex gap-2">
          {["day", "week", "month"].map(g => (
            <button key={g}
                    onClick={() => setGranularity(g)}
                    data-testid={`trend-granularity-${g}`}
                    className={`px-3 py-1 text-xs uppercase font-bold rounded-full border-2 border-[var(--ink)] ${
                      granularity === g ? "bg-[var(--ink)] text-white" : "bg-white"}`}>
              {g}
            </button>
          ))}
        </div>
      </div>
      {loading && <div className="text-sm">Loading…</div>}
      {err && <div className="text-sm text-red-700">{err}</div>}
      {data && (
        <>
          <div className="text-[10px] uppercase text-[var(--smoke)] tracking-widest">
            Preview data · reconciles to summary · unknown categories preserved
          </div>
          <div className="text-xs">
            Totals: <b>{data.totals.orders}</b> orders · <b>{DOLLAR(data.totals.customer_total)}</b> customer total ·
            <b> {DOLLAR(data.totals.amount_paid)}</b> amount paid ·
            <b> {data.totals.items_sold}</b> items sold
            {data.unbucketed_orders > 0 && (
              <span className="ml-2 text-yellow-800" data-testid="trend-unbucketed">
                · {data.unbucketed_orders} order(s) had no bucketable date and are shown separately
              </span>
            )}
          </div>
          <div className="grid gap-1 mt-2" data-testid="trend-buckets">
            {data.buckets.length === 0 && (
              <div className="text-xs text-[var(--smoke)] italic">No orders in this window.</div>
            )}
            {data.buckets.map(b => (
              <div key={b.bucket}
                   className="grid grid-cols-[110px_1fr_120px] items-center gap-2"
                   data-testid={`trend-row-${b.bucket}`}>
                <div className="font-mono text-xs">{b.bucket}</div>
                <div className="h-4 bg-neutral-100 rounded relative overflow-hidden">
                  <div className="absolute inset-y-0 left-0 bg-emerald-600"
                       style={{ width: `${(b.customer_total / maxCT) * 100}%` }} />
                </div>
                <div className="text-xs text-right font-mono">{DOLLAR(b.customer_total)}</div>
              </div>
            ))}
          </div>
        </>
      )}
    </section>
  );
}

// ── Group-by panel (payment/fulfillment) with drill-down ─────────
export function GroupByPanel({
  preset, dateBasis, endpoint, drilldownEndpoint, labelKey, title, testId,
}) {
  const { data, err, loading } = useSalesEndpoint(
    `/admin/sales/${endpoint}?preset=${preset}&date_basis=${dateBasis}`,
    [preset, dateBasis, endpoint],
  );
  const [drill, setDrill] = useState(null);
  const rows = useMemo(() => {
    if (!data) return [];
    return Object.entries(data.by_method || {})
      .map(([k, v]) => ({ [labelKey]: k, ...v }))
      .sort((a, b) => b.customer_total - a.customer_total);
  }, [data, labelKey]);
  return (
    <section className="rounded-2xl border-2 border-[var(--ink)] bg-white p-4 space-y-3"
             data-testid={testId}>
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h2 className="font-display text-lg">{title}</h2>
        <div className="text-[10px] uppercase text-[var(--smoke)] tracking-widest">
          Preview data · unknown categories preserved · click a row to drill down
        </div>
      </div>
      {loading && <div className="text-sm">Loading…</div>}
      {err && <div className="text-sm text-red-700">{err}</div>}
      {data && (
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[10px] uppercase text-[var(--smoke)]">
              <th className="pb-1">{title}</th>
              <th className="pb-1 text-right">Orders</th>
              <th className="pb-1 text-right">Customer Total</th>
              <th className="pb-1 text-right">Amount Paid</th>
              <th className="pb-1 text-right">Tax</th>
              <th className="pb-1 text-right">Shipping</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(r => (
              <tr key={r[labelKey]}
                  className="border-t border-neutral-100 cursor-pointer hover:bg-neutral-50"
                  data-testid={`${testId}-row-${r[labelKey]}`}
                  onClick={() => setDrill(r[labelKey])}>
                <td className={`py-1 ${r[labelKey] === "unknown" ? "italic text-yellow-800" : ""}`}>
                  {r[labelKey] === "unknown" ? UNKNOWN_LABEL : r[labelKey]}
                </td>
                <td className="py-1 text-right font-mono">{r.orders}</td>
                <td className="py-1 text-right font-mono">{DOLLAR(r.customer_total)}</td>
                <td className="py-1 text-right font-mono">{DOLLAR(r.amount_paid)}</td>
                <td className="py-1 text-right font-mono">{DOLLAR(r.tax_collected)}</td>
                <td className="py-1 text-right font-mono">{DOLLAR(r.shipping_charged)}</td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr><td colSpan={6} className="py-3 text-xs italic text-[var(--smoke)]">
                No orders in this window.
              </td></tr>
            )}
          </tbody>
          <tfoot>
            <tr className="border-t-2 border-[var(--ink)]">
              <td className="py-1 font-bold text-xs">TOTAL</td>
              <td className="py-1 text-right font-mono">{data.totals.orders}</td>
              <td className="py-1 text-right font-mono">{DOLLAR(data.totals.customer_total)}</td>
              <td className="py-1 text-right font-mono">{DOLLAR(data.totals.amount_paid)}</td>
              <td className="py-1 text-right"></td>
              <td className="py-1 text-right"></td>
            </tr>
          </tfoot>
        </table>
      )}
      {drill && (
        <DrillDownModal
          title={`${title} · ${drill === "unknown" ? UNKNOWN_LABEL : drill}`}
          path={`/admin/sales/${drilldownEndpoint}?method=${encodeURIComponent(drill)}&preset=${preset}&date_basis=${dateBasis}&limit=500`}
          onClose={() => setDrill(null)} />
      )}
    </section>
  );
}

// ── Customers panel ──────────────────────────────────────────────
export function CustomersPanel({ preset, dateBasis }) {
  const [sortBy, setSortBy] = useState("spend_desc");
  const { data, err, loading } = useSalesEndpoint(
    `/admin/sales/customers?preset=${preset}&date_basis=${dateBasis}&sort_by=${sortBy}&limit=100`,
    [preset, dateBasis, sortBy],
  );
  const [drill, setDrill] = useState(null);
  return (
    <section className="rounded-2xl border-2 border-[var(--ink)] bg-white p-4 space-y-3"
             data-testid="customers-panel">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h2 className="font-display text-lg">Customers · top 100</h2>
        <div className="flex items-center gap-2">
          <label className="text-[10px] uppercase text-[var(--smoke)] tracking-widest">Sort by</label>
          <select
            className="border-2 border-[var(--ink)] rounded-md px-2 py-1 text-xs bg-white"
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            data-testid="customers-sort-by"
          >
            <option value="name_asc">Name A–Z</option>
            <option value="name_desc">Name Z–A</option>
            <option value="orders_desc">Total Orders — High to Low</option>
            <option value="spend_desc">Total Spend — High to Low</option>
            <option value="last_order_desc">Most Recent Order</option>
          </select>
        </div>
      </div>
      {loading && <div className="text-sm">Loading…</div>}
      {err && <div className="text-sm text-red-700">{err}</div>}
      {data && (
        <>
          <div className="text-xs" data-testid="customers-totals">
            Totals: <b>{data.totals.unique_customers}</b> unique customer keys ·
            <b> {data.totals.orders}</b> orders ·
            <b> {DOLLAR(data.totals.customer_total)}</b> customer total
          </div>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[10px] uppercase text-[var(--smoke)]">
                <th className="pb-1">Customer</th>
                <th className="pb-1">Name</th>
                <th className="pb-1 text-right">Orders</th>
                <th className="pb-1 text-right">Customer Total</th>
                <th className="pb-1 text-right">Items</th>
                <th className="pb-1 text-right">Last Order</th>
              </tr>
            </thead>
            <tbody>
              {data.rows.map(r => (
                <tr key={r.customer_key}
                    className="border-t border-neutral-100 cursor-pointer hover:bg-neutral-50"
                    data-testid={`customer-row-${r.customer_key}`}
                    onClick={() => setDrill(r.customer_key)}>
                  <td className={`py-1 font-mono text-xs ${r.customer_key === "unknown" ? "italic text-yellow-800" : ""}`}>
                    {r.customer_key === "unknown" ? UNKNOWN_LABEL : r.customer_email_raw || r.customer_key}
                  </td>
                  <td className="py-1 text-xs" data-testid={`customer-name-${r.customer_key}`}>
                    {r.customer_display_name || <span className="italic text-yellow-800">Unknown Customer</span>}
                  </td>
                  <td className="py-1 text-right font-mono">{r.orders}</td>
                  <td className="py-1 text-right font-mono">{DOLLAR(r.customer_total)}</td>
                  <td className="py-1 text-right font-mono">{r.items_sold}</td>
                  <td className="py-1 text-right font-mono text-xs text-[var(--smoke)]">
                    {r.last_order_at ? new Date(r.last_order_at).toLocaleDateString() : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
      {drill && (
        <DrillDownModal
          title={`Customer · ${drill === "unknown" ? UNKNOWN_LABEL : drill}`}
          path={`/admin/sales/customers-drilldown?customer_key=${encodeURIComponent(drill)}&preset=${preset}&date_basis=${dateBasis}&limit=500`}
          onClose={() => setDrill(null)} />
      )}
    </section>
  );
}

// ── Products panel · P0 Products Analytics (2026-02-16) ─────────
// Backend contract: `/admin/sales/products-report` returns the full
// canonical catalog for the selected variant LEFT-JOINed with the
// window's sold quantities/revenue/orders and a historical price-
// bucket rollup. Zero-sale rows STAY visible so the owner can spot
// dead inventory. Gold catalog may not yet be loaded — the API returns
// `catalog_status:"not_loaded"` for that case; UI renders an
// empty-configuration message rather than fabricating rows.
const CENTS_TO_STR = (c) => c == null
  ? "—"
  : (Number(c) / 100).toLocaleString("en-US", { style: "currency", currency: "USD" });

const SORT_OPTIONS = [
  { value: "qty_desc",       label: "Quantity Sold — High to Low" },
  { value: "qty_asc",        label: "Quantity Sold — Low to High" },
  { value: "revenue_desc",   label: "Revenue — High to Low" },
  { value: "revenue_asc",    label: "Revenue — Low to High" },
  { value: "orders_desc",    label: "Orders — High to Low" },
  { value: "orders_asc",     label: "Orders — Low to High" },
  { value: "code_album",     label: "Sticker Code — Album Order" },
  { value: "code_asc",       label: "Sticker Code — A–Z" },
  { value: "player_asc",     label: "Player / Subject — A–Z" },
  { value: "player_desc",    label: "Player / Subject — Z–A" },
  { value: "last_sold_desc", label: "Last Sold — Most Recent" },
  { value: "last_sold_asc",  label: "Last Sold — Oldest" },
  { value: "never_or_low",   label: "Never Sold / Lowest Activity" },
];

const HEADER_SORT_MAP = {
  qty:     ["qty_desc", "qty_asc"],
  revenue: ["revenue_desc", "revenue_asc"],
  orders:  ["orders_desc", "orders_asc"],
};

function SortArrow({ active, direction }) {
  if (!active) return <span className="opacity-30 ml-1">↕</span>;
  return <span className="ml-1" data-testid="sort-active-arrow">{direction === "asc" ? "▲" : "▼"}</span>;
}

export function ProductsPanel({ preset, dateBasis }) {
  const [variant, setVariant] = useState("white");
  const [sortBy, setSortBy] = useState("qty_desc");
  const [filterMode, setFilterMode] = useState("all");
  const [search, setSearch] = useState("");
  const [priceBucketFilter, setPriceBucketFilter] = useState(""); // sold price
  const [currentPriceFilter, setCurrentPriceFilter] = useState(""); // catalog price
  const [pageSize, setPageSize] = useState(50);
  const [offset, setOffset] = useState(0);

  const queryStr = useMemo(() => {
    const p = new URLSearchParams({
      variant, preset, date_basis: dateBasis, sort_by: sortBy,
      filter_mode: filterMode, limit: String(pageSize), offset: String(offset),
    });
    if (search) p.set("search", search);
    if (priceBucketFilter) p.set("unit_price_cents", priceBucketFilter);
    if (currentPriceFilter) p.set("current_price_cents", currentPriceFilter);
    return `/admin/sales/products-report?${p.toString()}`;
  }, [variant, preset, dateBasis, sortBy, filterMode, search, priceBucketFilter, currentPriceFilter, pageSize, offset]);
  const { data, err, loading } = useSalesEndpoint(queryStr, [queryStr]);

  useEffect(() => { setOffset(0); }, [variant, sortBy, filterMode, search, priceBucketFilter, currentPriceFilter, pageSize]);

  const activeHeaderKey = (() => {
    for (const [k, [d, a]] of Object.entries(HEADER_SORT_MAP)) {
      if (sortBy === d) return { key: k, direction: "desc" };
      if (sortBy === a) return { key: k, direction: "asc" };
    }
    return { key: null, direction: null };
  })();

  const toggleHeader = (key) => {
    const [d, a] = HEADER_SORT_MAP[key];
    setSortBy(sortBy === d ? a : d);
  };

  const cat = data?.catalog || {};
  const rows = data?.products || [];
  const buckets = data?.price_buckets || [];
  const bucketOptions = useMemo(() => {
    const seen = new Set(buckets.map((b) => b.unit_price_cents));
    return Array.from(seen).sort((a, b) => a - b);
  }, [buckets]);
  const currentPriceOptions = useMemo(() => {
    // Distinct current-catalog prices seen in the currently-fetched page.
    const seen = new Set(rows.map((r) => r.current_price_cents).filter(Boolean));
    return Array.from(seen).sort((a, b) => a - b);
  }, [rows]);

  const notLoaded = data?.catalog_status === "not_loaded";

  return (
    <section className="rounded-2xl border-2 border-[var(--ink)] bg-white p-4 space-y-3"
             data-testid="products-panel">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h2 className="font-display text-lg">Products · full catalog + sales</h2>
        <div className="flex gap-1" data-testid="products-variant-tabs">
          {["white","blue","gold"].map((v) => (
            <button key={v}
              onClick={() => setVariant(v)}
              className={`px-3 py-1 text-xs uppercase font-bold border-2 border-[var(--ink)] rounded-full ${variant === v ? "bg-[var(--ink)] text-white" : "bg-white"}`}
              data-testid={`products-variant-${v}`}>
              {v}
            </button>
          ))}
        </div>
      </div>

      {/* Catalog counts */}
      {data && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-2 text-xs" data-testid="products-catalog-counts">
          <div className="rounded-md border p-2">
            <div className="uppercase text-[9px] text-[var(--smoke)]">Catalog total</div>
            <div className="font-mono text-base" data-testid="products-catalog-total">{cat.total}</div>
          </div>
          <div className="rounded-md border p-2">
            <div className="uppercase text-[9px] text-[var(--smoke)]">Sold ≥1 in period</div>
            <div className="font-mono text-base" data-testid="products-catalog-sold">{cat.sold}</div>
          </div>
          <div className="rounded-md border p-2">
            <div className="uppercase text-[9px] text-[var(--smoke)]">No sales in period</div>
            <div className="font-mono text-base" data-testid="products-catalog-nosales-period">{cat.no_sales_in_period ?? cat.never_sold}</div>
          </div>
          <div className="rounded-md border p-2">
            <div className="uppercase text-[9px] text-[var(--smoke)]">Never sold — all time</div>
            <div className="font-mono text-base" data-testid="products-catalog-neversold-alltime">{cat.never_sold_all_time ?? "—"}</div>
          </div>
          <div className="rounded-md border p-2">
            <div className="uppercase text-[9px] text-[var(--smoke)]">Coverage in period</div>
            <div className="font-mono text-base">{cat.coverage_pct}%</div>
          </div>
        </div>
      )}

      {notLoaded && (
        <div className="rounded-md border-2 border-yellow-500 bg-yellow-50 p-3 text-sm text-yellow-900"
             data-testid="products-gold-not-loaded">
          Gold pricing catalog has not been loaded yet. When the Gold catalog is imported this tab
          will automatically populate — no code changes required.
        </div>
      )}

      {!notLoaded && (
        <>
          {/* Filter row */}
          <div className="flex flex-wrap gap-2 items-end">
            <input type="text" value={search} onChange={(e) => setSearch(e.target.value)}
                   placeholder="Search code / player / country" data-testid="products-search"
                   className="border-2 border-[var(--ink)] rounded-full px-3 py-1 text-sm flex-1 min-w-[180px]" />
            <select value={sortBy} onChange={(e) => setSortBy(e.target.value)}
                    className="border-2 border-[var(--ink)] rounded-full px-3 py-1 text-xs"
                    data-testid="products-sort-by">
              {SORT_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
            <select value={filterMode} onChange={(e) => setFilterMode(e.target.value)}
                    className="border-2 border-[var(--ink)] rounded-full px-3 py-1 text-xs"
                    data-testid="products-filter-mode">
              <option value="all">All products</option>
              <option value="no_sales_in_period">No sales in period</option>
              <option value="never_sold_all_time">Never sold — all time</option>
              <option value="low_sales">Low sales (1–2 units)</option>
              <option value="top_sellers">Top sellers</option>
            </select>
            <select value={currentPriceFilter} onChange={(e) => setCurrentPriceFilter(e.target.value)}
                    className="border-2 border-[var(--ink)] rounded-full px-3 py-1 text-xs"
                    data-testid="products-current-price-filter"
                    title="Filter catalog rows by current master price">
              <option value="">Current price — all</option>
              {currentPriceOptions.map((c) => (
                <option key={c} value={c}>{CENTS_TO_STR(c)}</option>
              ))}
            </select>
            <select value={priceBucketFilter} onChange={(e) => setPriceBucketFilter(e.target.value)}
                    className="border-2 border-[var(--ink)] rounded-full px-3 py-1 text-xs"
                    data-testid="products-sold-price-filter"
                    title="Filter to sales matching this historical unit price">
              <option value="">Sold at — all prices</option>
              {bucketOptions.map((c) => (
                <option key={c} value={c}>{CENTS_TO_STR(c)}</option>
              ))}
            </select>
            <select value={pageSize} onChange={(e) => setPageSize(Number(e.target.value))}
                    className="border-2 border-[var(--ink)] rounded-full px-3 py-1 text-xs"
                    data-testid="products-page-size">
              <option value={25}>25/pg</option>
              <option value={50}>50/pg</option>
              <option value={100}>100/pg</option>
            </select>
          </div>

          {loading && <div className="text-sm">Loading…</div>}
          {err && <div className="text-sm text-red-700">{err}</div>}

          {/* Price buckets */}
          {data && buckets.length > 0 && (
            <div className="rounded-md border-2 border-[var(--ink)] p-3 bg-[var(--paper)]"
                 data-testid="products-price-buckets">
              <div className="flex items-baseline justify-between flex-wrap gap-2">
                <div className="text-[10px] uppercase font-bold tracking-widest">
                  {variant.toUpperCase()} — Sales by sold unit price (historical, frozen)
                </div>
                <div className="text-[10px] text-[var(--smoke)] italic">Uses the frozen order price — never today&apos;s master price.</div>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-2 mt-2">
                {buckets.map((b) => (
                  <div key={b.unit_price_cents}
                       className={`rounded-md border p-2 text-xs cursor-pointer ${priceBucketFilter === String(b.unit_price_cents) ? "border-2 border-[var(--ink)] bg-white" : "border-neutral-200"}`}
                       onClick={() => setPriceBucketFilter(priceBucketFilter === String(b.unit_price_cents) ? "" : String(b.unit_price_cents))}
                       data-testid={`price-bucket-${b.unit_price_cents}`}>
                    <div className="font-mono text-sm font-bold">{CENTS_TO_STR(b.unit_price_cents)}</div>
                    <div className="font-mono">{b.qty} units</div>
                    <div className="font-mono">{CENTS_TO_STR(b.revenue_cents)}</div>
                    <div className="text-[10px] text-[var(--smoke)]">{b.pct_of_variant_units}% of variant units</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Table */}
          {data && (
            <table className="w-full text-sm" data-testid="products-table">
              <thead>
                <tr className="text-left text-[10px] uppercase text-[var(--smoke)]">
                  <th className="pb-1">Code</th>
                  <th className="pb-1">Player / Subject</th>
                  <th className="pb-1 text-right">Current price</th>
                  <th className="pb-1 text-right cursor-pointer select-none"
                      onClick={() => toggleHeader("qty")}
                      data-testid="products-th-qty">
                    Qty <SortArrow active={activeHeaderKey.key === "qty"} direction={activeHeaderKey.direction} />
                  </th>
                  <th className="pb-1 text-right cursor-pointer select-none"
                      onClick={() => toggleHeader("revenue")}
                      data-testid="products-th-revenue">
                    Revenue <SortArrow active={activeHeaderKey.key === "revenue"} direction={activeHeaderKey.direction} />
                  </th>
                  <th className="pb-1 text-right cursor-pointer select-none"
                      onClick={() => toggleHeader("orders")}
                      data-testid="products-th-orders">
                    Orders <SortArrow active={activeHeaderKey.key === "orders"} direction={activeHeaderKey.direction} />
                  </th>
                  <th className="pb-1 text-right">Avg sold price</th>
                  <th className="pb-1 text-right">Last sold</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.code}
                      className={`border-t border-neutral-100 ${r.qty_sold === 0 ? "opacity-70" : ""}`}
                      data-testid={`product-row-${r.code}`}>
                    <td className="py-1 font-mono text-xs">{r.code}</td>
                    <td className="py-1 text-xs">{r.player_name || "—"}{r.country_code ? <span className="text-[var(--smoke)] ml-1">· {r.country_code}</span> : null}</td>
                    <td className="py-1 text-right font-mono text-xs">{CENTS_TO_STR(r.current_price_cents)}</td>
                    <td className="py-1 text-right font-mono">{r.qty_sold}</td>
                    <td className="py-1 text-right font-mono">{CENTS_TO_STR(r.revenue_cents)}</td>
                    <td className="py-1 text-right font-mono">{r.orders}</td>
                    <td className="py-1 text-right font-mono text-xs">{r.avg_realized_unit_price_cents == null ? "—" : CENTS_TO_STR(r.avg_realized_unit_price_cents)}</td>
                    <td className="py-1 text-right font-mono text-xs">{r.last_sold_at ? new Date(r.last_sold_at).toLocaleDateString() : <span className="italic text-[var(--smoke)]">Never</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {/* Pagination */}
          {data && data.row_count > pageSize && (
            <div className="flex items-center justify-between text-xs" data-testid="products-pager">
              <button className="border-2 border-[var(--ink)] rounded-full px-3 py-1 disabled:opacity-40"
                      disabled={offset === 0}
                      onClick={() => setOffset(Math.max(0, offset - pageSize))}
                      data-testid="products-prev">Prev</button>
              <span>{offset + 1}–{Math.min(offset + pageSize, data.row_count)} of {data.row_count}</span>
              <button className="border-2 border-[var(--ink)] rounded-full px-3 py-1 disabled:opacity-40"
                      disabled={offset + pageSize >= data.row_count}
                      onClick={() => setOffset(offset + pageSize)}
                      data-testid="products-next">Next</button>
            </div>
          )}
        </>
      )}
    </section>
  );
}

// ── Reusable drill-down modal ────────────────────────────────────
export function DrillDownModal({ title, path, onClose }) {
  const { data, err, loading } = useSalesEndpoint(path, [path]);
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
         data-testid="drill-modal" onClick={onClose}>
      <div className="w-full max-w-4xl max-h-[90vh] overflow-auto bg-white rounded-2xl border-2 border-[var(--ink)] shadow-[6px_6px_0_var(--ink)] p-5"
           onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h3 className="font-display text-xl">{title}</h3>
          <button onClick={onClose} data-testid="drill-modal-close"
                  className="text-xs uppercase font-bold">Close</button>
        </div>
        {loading && <div className="text-sm mt-2">Loading…</div>}
        {err && <div className="text-sm text-red-700 mt-2">{err}</div>}
        {data && (
          <>
            <div className="text-xs text-[var(--smoke)] mt-2" data-testid="drill-modal-summary">
              {data.row_count} row(s){data.customer_total_cents != null && (
                <> · customer total = <b>{DOLLAR(data.customer_total_cents)}</b></>
              )}
              {data.matched_revenue_cents != null && (
                <> · matched revenue = <b>{DOLLAR(data.matched_revenue_cents)}</b>
                   · matched qty = <b>{data.matched_quantity}</b></>
              )}
            </div>
            <table className="w-full mt-3 text-sm">
              <thead>
                <tr className="text-left text-[10px] uppercase text-[var(--smoke)]">
                  <th className="pb-1">Order</th>
                  <th className="pb-1">Type</th>
                  <th className="pb-1">Status</th>
                  <th className="pb-1">Payment</th>
                  <th className="pb-1">Fulfillment</th>
                  <th className="pb-1 text-right">Customer Total</th>
                </tr>
              </thead>
              <tbody>
                {(data.rows || []).map((r, i) => (
                  <tr key={i} className="border-t border-neutral-100">
                    <td className="py-1 font-mono text-xs">{r.public_order_ref}</td>
                    <td className="py-1 text-xs">{r.source_type}</td>
                    <td className="py-1 text-xs">{r.order_status || "—"}</td>
                    <td className="py-1 text-xs">{r.payment_method || "—"}</td>
                    <td className="py-1 text-xs">{r.fulfillment_method || "—"}</td>
                    <td className="py-1 text-right font-mono">{DOLLAR(r.customer_total)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}
      </div>
    </div>
  );
}
